import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import map from 'lodash/map';
import find from 'lodash/find';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import isUndefined from 'lodash/isUndefined';
import {
  API,
} from 'vibeguide';
import PermissionGroup from './PermissionGroup';
import './PermissionList.scss';

// the sysAdmin and ismAdmin permission IDs are set when all permissions are fetched
let sysAdminPermissionId = null;
let ismAdminPermissionId = null;

function PermissionList({
  className = '',
  selectedAccountType = null,
  permissionSetId = null,
  permissionIds = [],
  disabled = false,
  bulk = false,
  updatePermissions = [],
  onUpdate = () => {},
}) {
  const [allPermissions, setAllPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [admin, setSysAdmin] = useState({
    sysAdmin: false,
    ismAdmin: false,
  });

  /**
   * Get selected permission data for a group of permissions
   */
  const getGroupPermissions = (groupTitle) => {
    // group permission by title
    const groupPermissions = permissions.filter(permission => permission.friendlyType === groupTitle);
    const selectedPermissions = groupPermissions.filter(permission => permission.selected);
    const selectedIds = selectedPermissions.map(permission => permission._id);
    const allSelected = selectedPermissions.length === groupPermissions.length;
    const someSelected = selectedPermissions.length > 0 && !allSelected;

    // bulk group permission settings
    const bulkOnPermissions = groupPermissions.filter(permission => permission.on === true);
    const bulkOn = bulkOnPermissions.length === groupPermissions.length;
    const bulkOffPermissions = groupPermissions.filter(permission => permission.off === true);
    const bulkOff = bulkOffPermissions.length === groupPermissions.length;

    return {
      groupPermissions,
      selectedPermissions,
      selectedIds,
      allSelected,
      someSelected,
      bulkOn,
      bulkOff,
    };
  };

  /**
   * When a permission group is toggled
   */
  const onToggleGroup = (groupTitle) => {
    const group = getGroupPermissions(groupTitle);

    if (group.allSelected || group.someSelected) {
      // turn off all permissions in group
      const updatedPermissions = permissions.map(permission => {
        if (permission.friendlyType === groupTitle) {
          return {
            ...permission,
            selected: false,
          };
        }
        return permission;
      });

      setPermissions(updatedPermissions);
    } else {
      // turn on all permissions in group
      const updatedPermissions = permissions.map(permission => {
        if (permission.friendlyType === groupTitle) {
          return {
            ...permission,
            selected: true,
          };
        }
        return permission;
      });

      setPermissions(updatedPermissions);
    }
  };

  /**
   * When a single permission is toggled
   */
  const onTogglePermission = (groupTitle, permissionId) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission._id === permissionId) {
        return {
          ...permission,
          selected: !permission.selected,
        };
      }
      return permission;
    });

    setPermissions(updatedPermissions);
  };

  /**
   * When a permission group is set to on
   */
  const onBulkGroupOn = (groupTitle) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission.friendlyType === groupTitle) {
        return {
          ...permission,
          on: true,
          off: false,
        };
      }
      return permission;
    });

    setPermissions(updatedPermissions);

    const groupPermissionIds = permissions.filter(permission => permission.friendlyType === groupTitle)
      .map(permission => permission._id);

    onUpdate({
      updatePermissions: [
        ...updatePermissions.filter(permission => !groupPermissionIds.includes(permission._id)),
        ...permissions.filter(permission => permission.friendlyType === groupTitle).map(permission => {
          return {
            _id: permission._id,
            action: 'add',
          };
        }),
      ],
    });
  };

  /**
   * When a permission group is set to off
   */
  const onBulkGroupOff = (groupTitle) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission.friendlyType === groupTitle) {
        return {
          ...permission,
          on: false,
          off: true,
        };
      }
      return permission;
    });

    setPermissions(updatedPermissions);

    const groupPermissionIds = permissions.filter(permission => permission.friendlyType === groupTitle)
      .map(permission => permission._id);

    onUpdate({
      updatePermissions: [
        ...updatePermissions.filter(permission => !groupPermissionIds.includes(permission._id)),
        ...permissions.filter(permission => permission.friendlyType === groupTitle).map(permission => {
          return {
            _id: permission._id,
            action: 'remove',
          };
        }),
      ],
    });
  };

  /**
   * When an individual permission is set to 'on'
   */
  const onBulkPermissionOn = (groupTitle, permissionId) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission._id === permissionId) {
        return {
          ...permission,
          on: !permission.on,
          off: false,
        };
      }

      return permission;
    });

    setPermissions(updatedPermissions);

    // if the permission is already set to 'on', do not update the permission
    const data = find(updatePermissions, { _id: permissionId, action: 'add' })
      ? []
      : [{ _id: permissionId, action: 'add' }];

    onUpdate({
      updatePermissions: [
        ...updatePermissions.filter(permission => permission._id !== permissionId),
        ...data,
      ],
    });
  };

  /**
   * When an individual permission is set to 'off'
   */
  const onBulkPermissionOff = (groupTitle, permissionId) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission._id === permissionId) {
        return {
          ...permission,
          on: false,
          off: !permission.off,
        };
      }

      return permission;
    });

    setPermissions(updatedPermissions);

    // if the permission is already set to 'on', do not update the permission
    const data = find(updatePermissions, { _id: permissionId, action: 'remove' })
      ? []
      : [{ _id: permissionId, action: 'remove' }];

    onUpdate({
      updatePermissions: [
        ...updatePermissions.filter(permission => permission._id !== permissionId),
        ...data,
      ],
    });
  };

  /**
   * Filter Permissions based on selected account type and selected admin permissions
   */
  const filterPermissions = (permissionsList, {
    sysAdmin = false,
    ismAdmin = false,
  }) => {
    const filteredPermissions = permissionsList.filter((permission) => {
      if (bulk) {
        // in bulk edit mode, show all permissions
        return true;
      }

      switch (selectedAccountType) {
        case 'partnerAccount': {
          // filter out any permission that isn't for partner accounts
          if (permission.scopes.includes('partner')) {
            return true;
          }

          return false;
        }

        case 'userAccount':
        case 'systemAccount':
        default: {
          // filter out any permission that isn't for client accounts
          // always allow admin permissions to be shown
          // show admin scoped permissions if sysAdmin is true
          // show partner scoped permissions if ismAdmin is true
          if (permission.scopes.includes('client')
            || permission.ptype === 'admin'
            || (sysAdmin && permission.scopes.includes('admin'))
            || (ismAdmin && permission.scopes.includes('partner'))
          ) {
            return true;
          }

          return false;
        }
      }
    });

    return filteredPermissions;
  };

  /**
   * Get Permission List
   */
  const getAllPermissions = async () => {
    const permissionsList = await API.Permission.list();
    // set sysAdmin and ismAdmin permission IDs
    sysAdminPermissionId = get(find(permissionsList, { ptype: 'admin', pname: 'system_admin' }), '_id') || null;
    ismAdminPermissionId = get(find(permissionsList, { ptype: 'admin', pname: 'ism_admin' }), '_id') || null;
    setAllPermissions(permissionsList);
  };

  /**
   * Get permission set and update the selected permissions
   */
  const getPermissionSet = async (permissionSetId) => {
    const permissionSet = await API.PermissionSet.getById(permissionSetId);
    const permissionSetIds = permissionSet.permissions.map(permission => permission._id) || [];

    // check if the sysAdmin permission is selected
    const hasSysAdmin = permissionSetIds.includes(sysAdminPermissionId);
    const hasIsmAdmin = permissionSetIds.includes(ismAdminPermissionId);

    const filteredPermissions = filterPermissions(allPermissions, {
      sysAdmin: hasSysAdmin,
      ismAdmin: hasIsmAdmin,
    }).map(permission => {
      // select permissions that are in the permission set
      if (permissionSetIds.includes(permission._id)) {
        return {
          ...permission,
          selected: true,
        };
      }

      // deselect any permissions that are not in the permission set
      return {
        ...permission,
        selected: false,
      };
    });

    setPermissions(filteredPermissions);
  };

  /**
   * Get the permissions the user can set when the component mounts
   */
  useEffect(() => {
    async function fetchData() {
      await getAllPermissions();
    }
    fetchData();
  }, []);

  /**
   * Get the permission set when one is selected
   */
  useEffect(() => {
    async function fetchData() {
      await getPermissionSet(permissionSetId);
    }

    if (permissionSetId && allPermissions.length > 0) {
      fetchData();
    }
  }, [permissionSetId, allPermissions]);

  /**
   * Filter the permission list when all permissions is changed
   */
  useEffect(() => {
    if (allPermissions.length > 0) {
      // check if the sysAdmin permission is selected
      const hasSysAdmin = permissionIds.includes(sysAdminPermissionId);
      const hasIsmAdmin = permissionIds.includes(ismAdminPermissionId);

      const filteredPermissions = filterPermissions(allPermissions, {
        sysAdmin: hasSysAdmin,
        ismAdmin: hasIsmAdmin,
      }).map(permission => {
        return {
          ...permission,
          selected: permissionIds.includes(permission._id),
        };
      });

      setPermissions(filteredPermissions);
    }
  }, [allPermissions, admin]);

  /**
   * When the shown permissions change, update the parent component of selected permissions
   */
  useEffect(() => {
    if (permissions.length > 0) {
      // check if the sysAdmin and/or ismAdmin permission is selected
      const sysAdmin = !isUndefined(find(permissions, { _id: sysAdminPermissionId, selected: true }));
      const ismAdmin = !isUndefined(find(permissions, { _id: ismAdminPermissionId, selected: true }));

      // update parent component with selected permission IDs
      onUpdate({
        permissionIds: permissions.filter(permission => permission.selected).map(permission => permission._id),
      });

      // only update admin state if it has changed
      if (admin.sysAdmin !== sysAdmin || admin.ismAdmin !== ismAdmin) {
        setSysAdmin({
          sysAdmin,
          ismAdmin,
        });
      }
    }
  }, [permissions]);

  // render when permissions exist
  if (permissions.length <= 0) {
    return null;
  }

  const groups = {};

  permissions.forEach(permission => {
    if (!groups[permission.friendlyType]) {
      groups[permission.friendlyType] = [];
    }

    // add permission to group
    groups[permission.friendlyType].push(permission);
  });

  return (
    <div className={classNames('PermissionList', className)}>
      {map(groups, (groupPermissions, groupTitle) => {
        const {
          selectedIds,
          allSelected,
          someSelected,
          bulkOn,
          bulkOff,
        } = getGroupPermissions(groupTitle);

        return (
          <PermissionGroup
            key={`group-${groupTitle}`}
            title={groupTitle}
            permissions={sortBy(groupPermissions, ['sequence'])}
            selectedIds={selectedIds}
            allToggled={allSelected}
            someToggled={someSelected}
            disabled={disabled}
            bulk={bulk}
            bulkGroupOn={bulkOn}
            bulkGroupOff={bulkOff}
            onToggleGroup={onToggleGroup}
            onTogglePermission={onTogglePermission}
            onBulkGroupOn={onBulkGroupOn}
            onBulkGroupOff={onBulkGroupOff}
            onBulkPermissionOn={onBulkPermissionOn}
            onBulkPermissionOff={onBulkPermissionOff}
          />
        );
      })}
    </div>
  );
}

export default PermissionList;
